import { useMutation, useQueryClient } from "@tanstack/react-query";
import client from "api/graphql/getClient";
import { graphql } from "gql/gql";
import { DeleteAccountMemberParams } from "gql/graphql";
import { useActiveAccount } from "utils/useActiveAccount";
import useSnackbar from "utils/useSnackbar";

const deleteAccountMemberDocument = graphql(/* GraphQL */ `
	mutation deleteAccountMember($params: DeleteAccountMemberParams!) {
		deleteAccountMember(params: $params) {
			error {
				code
				message
			}
			success
		}
	}
`);

interface Args {
	params: DeleteAccountMemberParams;
	userId?: string;
}

const deleteAccountMember = ({ userId, params }: Args) =>
	client(userId)
		.request(deleteAccountMemberDocument, { params })
		.then((res) => res.deleteAccountMember);

const useDeleteAccountMember = () => {
	const { _id, userId } = useActiveAccount();
	const { handleError, handleSuccess } = useSnackbar();
	const queryClient = useQueryClient();

	return useMutation(
		(params: DeleteAccountMemberParams) =>
			deleteAccountMember({ params, userId }),
		{
			onSettled: () => {
				queryClient.invalidateQueries(
					["user", userId, "account", _id, "listAccountMembers"],
					{ exact: true }
				);
			},
			onSuccess: (data) => {
				if (data.error) {
					handleError(data.error);
				} else if (data.success) {
					handleSuccess("Account member has been deleted.");
				}
			},
		}
	);
};

export default useDeleteAccountMember;
